import { useEffect, useState } from 'react';
import { saveCompany, type Company } from './api';
import { useCompany } from './context';
import { useNamespaceCopy } from '@/i18n/ns';
import companySettingsCopy from '@/i18n/ns/company-settings';
import { useAppCatalogLocale } from '@/i18n/context';
import companyErrors from '@/i18n/ns/company-errors';

export function CompanyProfile({
  onEditStateChange,
}: {
  onEditStateChange: (state: { dirty: boolean; busy: boolean }) => void;
}) {
  const { data, adopt, refresh } = useCompany();
  const copy = useNamespaceCopy(companySettingsCopy);
  const locale = useAppCatalogLocale();
  const errors = useNamespaceCopy(companyErrors);
  const [draft, setDraft] = useState<Company | null>(data?.company ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setDraft(data?.company ?? null);
  }, [data]);
  useEffect(() => {
    onEditStateChange({
      dirty: Boolean(
        draft && data?.company && JSON.stringify(draft) !== JSON.stringify(data.company),
      ),
      busy,
    });
  }, [draft, data, busy, onEditStateChange]);
  if (!draft) return <p>{copy.profileUnavailable}</p>;
  const editable = data?.membership?.role === 'owner';
  const update = (key: keyof Company, value: string) => {
    setDraft({ ...draft, [key]: value });
    setSaved(false);
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      adopt(await saveCompany(draft, locale));
      setSaved(true);
    } catch (error) {
      setError(error instanceof Error ? error.message : errors.saveFailed);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <h1>{copy.companyProfile}</h1>
      <p className="company-description">
        {copy.profileDescription.replace('{company}', draft.name)}
      </p>
      <form
        className="company-form"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        {(
          [
            ['name', copy.companyName, 120],
            ['website', copy.website, 500],
            ['market', copy.primaryMarket, 80],
            ['language', copy.companyLanguage, 80],
          ] as const
        ).map(([key, label, maxLength]) => (
          <label key={key}>
            {label}
            <input
              required={key === 'name'}
              type={key === 'website' ? 'url' : 'text'}
              value={draft[key] ?? ''}
              disabled={!editable || busy}
              onChange={(event) => update(key, event.target.value)}
              maxLength={maxLength}
            />
          </label>
        ))}
        <label>
          {copy.companyMatters}
          <textarea
            value={draft.goal ?? ''}
            disabled={!editable || busy}
            onChange={(event) => update('goal', event.target.value)}
            maxLength={1000}
            rows={4}
          />
          <small>{copy.profileHint}</small>
        </label>
        {error && (
          <div role="alert" className="company-error">
            {error}{' '}
            <button type="button" className="company-link" onClick={() => void refresh()}>
              {copy.reloadCompany}
            </button>
          </div>
        )}
        {editable && (
          <div className="company-form-actions">
            <button className="company-primary" disabled={busy}>
              {busy ? copy.saving : copy.saveCompanyProfile}
            </button>
            {saved && <span role="status">{copy.profileSaved}</span>}
          </div>
        )}
      </form>
    </>
  );
}
